import React from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import type { TenantIntegration } from '@/api/integrations/integrationService';
import type { CatalogProvider } from './catalog';

interface ProviderPickerProps {
    providers: CatalogProvider[];
    value: string;
    onChange: (providerId: string) => void;
    /** Saved row per provider id. */
    rows: Record<string, TenantIntegration | undefined>;
    ariaLabel: string;
}

/**
 * Provider radio cards. A provider with a saved row is tagged "Active" or "Saved earlier"
 * (saved but off); picking another provider keeps the saved ones untouched.
 */
export const ProviderPicker: React.FC<ProviderPickerProps> = ({
    providers,
    value,
    onChange,
    rows,
    ariaLabel,
}) => {
    const { t } = useTranslation('crm');

    return (
        <RadioGroup
            value={value}
            onValueChange={onChange}
            aria-label={ariaLabel}
            className="grid gap-2 sm:grid-cols-2"
        >
            {providers.map(provider => {
                const row = rows[provider.id];
                const selected = provider.id === value;
                return (
                    <label
                        key={provider.id}
                        htmlFor={`provider-${provider.id}`}
                        className={cn(
                            'flex cursor-pointer items-start gap-3 rounded-lg border bg-background p-3 transition-colors hover:bg-surface-hover focus-within:ring-2 focus-within:ring-ring',
                            selected ? 'border-primary bg-primary/5' : 'border-border'
                        )}
                    >
                        <RadioGroupItem
                            id={`provider-${provider.id}`}
                            value={provider.id}
                            className="mt-0.5"
                        />
                        <span className="min-w-0 flex-1 space-y-0.5">
                            <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                                {provider.name}
                                {row && (
                                    <span
                                        className={cn(
                                            'rounded-full px-2 py-0.5 text-xs font-medium',
                                            row.is_active
                                                ? 'bg-success/10 text-success'
                                                : 'bg-muted text-muted-foreground'
                                        )}
                                    >
                                        {row.is_active
                                            ? t('integrations.picker.active')
                                            : t('integrations.picker.savedEarlier')}
                                    </span>
                                )}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                                {t(provider.description)}
                            </span>
                        </span>
                    </label>
                );
            })}
        </RadioGroup>
    );
};

export default ProviderPicker;
